import React from 'react';
import { formatMoney, formatNumber } from '../../../utils/formatters';
import { SectionHead } from './shared';
import { HandshakeIcon, CheckCircleIcon, HourglassIcon, GiftIcon } from './icons';

const Outcome = ({ icon, label, rate, sample, sub, tone }) => (
  <div className={`crma-outcome crma-outcome--${tone}`}>
    <span className="crma-outcome-icon">{icon}</span>
    <span className="crma-outcome-label">{label}</span>
    <span className="crma-outcome-value">{rate === null ? '—' : `${rate}%`}</span>
    <span className="crma-outcome-sub">{rate === null ? `мало данных (${formatNumber(sample)})` : sub}</span>
  </div>
);

const Vs = ({ value, base, suffix = '', money }) => {
  const up = value >= base;
  return (
    <span className={`crma-vs ${up ? 'up' : 'down'}`}>
      {up ? '↑' : '↓'} {money ? formatMoney(base) : `${base}${suffix}`} без тега
    </span>
  );
};

/**
 * Работает ли то, что делает персонал: закрытые авто-задачи против
 * открытых, win-back-предложения и теги. Пока данных нет — объясняем,
 * откуда они возьмутся, а не прячем блок молча.
 */
const EngagementBlock = ({ engagement }) => {
  const tasks = engagement?.task_outcomes;
  const hasTasks = tasks && (tasks.staff.sample + tasks.no_reaction.sample) > 0;
  const winBack = engagement?.win_back;
  const hasWinBack = winBack && winBack.total > 0;
  const tags = engagement?.tag_impact || [];

  return (
    <section className="panel crma-section">
      <SectionHead
        icon={<HandshakeIcon size={18} className="heading-icon" />}
        title="Эффект работы с гостями"
        sub="Вернулся ли гость после звонка, задачи или персонального предложения"
      />

      {!hasTasks && !hasWinBack && tags.length === 0 ? (
        <p className="crma-empty">
          Здесь появятся результаты, когда персонал начнёт закрывать авто-задачи по гостям
          под риском, отправлять win-back-предложения или отмечать гостей тегами.
        </p>
      ) : (
        <>
          {(hasTasks || hasWinBack) && (
            <div className="crma-outcomes">
              {hasTasks && (
                <>
                  {tasks.staff.sample > 0 ? (
                    <Outcome
                      tone="good" icon={<CheckCircleIcon size={18} />}
                      label="Персонал закрыл задачу" rate={tasks.staff.returned_rate} sample={tasks.staff.sample}
                      sub={`пришли после закрытия, из ${formatNumber(tasks.staff.sample)}`}
                    />
                  ) : (
                    <div className="crma-outcome crma-outcome--muted">
                      <span className="crma-outcome-icon"><CheckCircleIcon size={18} /></span>
                      <span className="crma-outcome-label">Персонал закрыл задачу</span>
                      <span className="crma-outcome-value">0</span>
                      <span className="crma-outcome-sub">вручную ещё не закрыто ни одной задачи — сравнивать не с чем</span>
                    </div>
                  )}
                  <Outcome
                    tone="muted" icon={<HourglassIcon size={18} />}
                    label="Без реакции персонала" rate={tasks.no_reaction.returned_rate} sample={tasks.no_reaction.sample}
                    sub={`вернулись сами, из ${formatNumber(tasks.no_reaction.sample)}`}
                  />
                </>
              )}
              {hasWinBack && (
                <Outcome
                  tone="accent" icon={<GiftIcon size={18} />}
                  label="Win-back предложение" rate={winBack.rate} sample={winBack.total}
                  sub={`${formatNumber(winBack.returned)} из ${formatNumber(winBack.total)} вернулись · рассылок: ${winBack.offers}`}
                />
              )}
            </div>
          )}
          {hasTasks && (
            <p className="crma-note">
              Авто-задача ставится, когда гость из топ-10% по тратам за год уходит в риск. «Без реакции» — задачи, которые
              никто не закрыл, в том числе {formatNumber(tasks.auto_closed)} закрытых системой, когда гость
              вернулся сам. У закрытых персоналом гость считается вернувшимся, только если пришёл после
              закрытия. Разница в пару пунктов на малой выборке — ещё не вывод.
              {tasks.archived > 0 && ` В архиве ${formatNumber(tasks.archived)} старых задач по неценным гостям — в расчёт не входят.`}
            </p>
          )}

          {tags.length > 0 && (
            <div className="crma-tags">
              <h3>Теги и удержание</h3>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Тег</th>
                      <th>Гостей</th>
                      <th>Траты за год на гостя</th>
                      <th>Активны сейчас</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tags.map((t) => (
                      <tr key={t.tag}>
                        <td><span className="ui-chip">{t.tag}</span></td>
                        <td>{formatNumber(t.guests)}</td>
                        <td>
                          {formatMoney(t.avg_spend)}{' '}
                          <Vs value={t.avg_spend} base={t.baseline_avg_spend} money />
                        </td>
                        <td>
                          {t.active_rate}%{' '}
                          <Vs value={t.active_rate} base={t.baseline_active_rate} suffix="%" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default EngagementBlock;
